import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Sparkles,
  Search,
  ExternalLink,
  Star,
  TrendingUp,
  Filter,
  Wand2,
  BarChart3,
  MessageSquare,
  FileText,
  Image,
  Code,
  Users,
  DollarSign,
  Megaphone,
  Scale,
  Briefcase,
  HeartPulse,
  Cog,
  Zap,
  Crown,
  Check,
  Heart,
  Globe,
  ThumbsUp,
  ThumbsDown,
  Lightbulb,
  Link2,
  Calendar,
  Building2,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Separator } from '@/components/ui/separator';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { useAIToolsCatalog, type AIToolCatalogRow } from '@/hooks/useAIToolsCatalog';
import { supabase } from '@/integrations/supabase/client';
import RegisterToViewModal from '@/components/public/RegisterToViewModal';

interface AITool {
  id: string;
  name: string;
  description: string;
  fullDescription: string;
  category: string;
  
  icon: React.ReactNode;
  rating: number;
  trending: boolean;
  featured: boolean;
  pricing: 'free' | 'freemium' | 'paid';
  url: string;
  tags: string[];
  useCases: string[];
  pros: string[];
  cons: string[];
  integrations: string[];
  founded: string;
  company: string;
}

const categoryIconMap: Record<string, React.ReactNode> = {
  content: <FileText className="w-6 h-6" />,
  analysis: <BarChart3 className="w-6 h-6" />,
  assistant: <MessageSquare className="w-6 h-6" />,
  image: <Image className="w-6 h-6" />,
  code: <Code className="w-6 h-6" />,
  automation: <Cog className="w-6 h-6" />,
};

function mapRowToTool(row: AIToolCatalogRow): AITool {
  return {
    id: row.id,
    name: row.name,
    description: row.description || '',
    fullDescription: row.full_description || row.description || '',
    category: row.category || 'assistant',
    
    icon: categoryIconMap[row.category || ''] || <Sparkles className="w-6 h-6" />,
    rating: Number(row.rating) || 0,
    trending: row.trending || false,
    featured: row.featured || false,
    pricing: (row.pricing as 'free' | 'freemium' | 'paid') || 'paid',
    url: row.website_url || '',
    tags: row.tags || [],
    useCases: (row.use_cases as string[]) || [],
    pros: (row.pros as string[]) || [],
    cons: (row.cons as string[]) || [],
    integrations: (row.integrations as string[]) || [],
    founded: row.founded || '',
    company: row.company || row.vendor || '',
  };
}

const categories = [
  { id: 'all', label: 'Todas las categorías', icon: <Sparkles className="w-4 h-4" /> },
  { id: 'content', label: 'Generación de contenido', icon: <FileText className="w-4 h-4" /> },
  { id: 'analysis', label: 'Análisis de datos', icon: <BarChart3 className="w-4 h-4" /> },
  { id: 'assistant', label: 'Asistentes virtuales', icon: <MessageSquare className="w-4 h-4" /> },
  { id: 'image', label: 'Generación de imágenes', icon: <Image className="w-4 h-4" /> },
  { id: 'code', label: 'Desarrollo y código', icon: <Code className="w-4 h-4" /> },
  { id: 'automation', label: 'Automatización', icon: <Cog className="w-4 h-4" /> },
];




// mockTools removed — data now comes from useAIToolsCatalog hook

const pricingColors = {
  free: 'bg-success/20 text-success border-success/30',
  freemium: 'bg-info/20 text-info border-info/30',
  paid: 'bg-warning/20 text-warning border-warning/30',
};

const pricingLabels = {
  free: 'Gratis',
  freemium: 'Freemium',
  paid: 'De pago',
};

export default function AIToolsPage({ isPublic = false }: { isPublic?: boolean }) {
  const { data: catalogRows, isLoading, isError } = useAIToolsCatalog();
  const allTools = useMemo(() => (catalogRows ?? []).map(mapRowToTool), [catalogRows]);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  const [favorites, setFavorites] = useState<string[]>([]);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [showFeaturedOnly, setShowFeaturedOnly] = useState(false);
  const [selectedTool, setSelectedTool] = useState<AITool | null>(null);
  const [showRecommendDialog, setShowRecommendDialog] = useState(false);
  const [recommendQuery, setRecommendQuery] = useState('');
  const [isSearchingAI, setIsSearchingAI] = useState(false);
  const [recommendedTools, setRecommendedTools] = useState<{ tool_id: string; reason: string }[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const itemsPerPage = 24;

  // Load favorites from localStorage on mount
  useEffect(() => {
    const savedFavorites = localStorage.getItem('ai-tools-favorites');
    if (savedFavorites) {
      setFavorites(JSON.parse(savedFavorites));
    }
  }, []);

  // Save favorites to localStorage when they change
  useEffect(() => {
    localStorage.setItem('ai-tools-favorites', JSON.stringify(favorites));
  }, [favorites]);

  const toggleFavorite = (toolId: string, toolName: string) => {
    setFavorites((prev) => {
      if (prev.includes(toolId)) {
        toast.success(`${toolName} eliminado de favoritos`);
        return prev.filter((id) => id !== toolId);
      } else {
        toast.success(`${toolName} agregado a favoritos`);
        return [...prev, toolId];
      }
    });
  };

  const handleAIRecommend = async () => {
    if (!recommendQuery.trim()) return;
    setIsSearchingAI(true);
    try {
      const { data, error } = await supabase.functions.invoke('recommend-tools', {
        body: { query: recommendQuery },
      });
      if (error) throw error;
      if (data?.error) {
        toast.error(data.error);
        return;
      }
      const recs: { tool_id: string; reason: string }[] = data?.recommendations ?? [];
      if (recs.length === 0) {
        toast.info('No se encontraron recomendaciones para tu consulta');
        return;
      }
      setRecommendedTools(recs);
      setShowRecommendDialog(false);
      setRecommendQuery('');
      // Reset filters to show all tools so recommended ones are visible
      setSearchQuery('');
      setSelectedCategory('all');
      
      setShowFavoritesOnly(false);
      setShowFeaturedOnly(false);
      toast.success(`${recs.length} herramientas recomendadas por IA`);
    } catch (err: any) {
      console.error(err);
      toast.error('Error al obtener recomendaciones. Intenta de nuevo.');
    } finally {
      setIsSearchingAI(false);
    }
  };

  const filteredTools = allTools.filter((tool) => {
    const matchesSearch =
      tool.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tool.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tool.tags.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesCategory = selectedCategory === 'all' || tool.category === selectedCategory;
    const matchesFavorites = !showFavoritesOnly || favorites.includes(tool.id);
    const matchesFeatured = !showFeaturedOnly || tool.featured;

    return matchesSearch && matchesCategory && matchesFavorites && matchesFeatured;
  });

  // If AI recommendations are active, show ONLY recommended tools
  const sortedTools = useMemo(() => {
    if (recommendedTools.length === 0) return filteredTools;
    const recIds = new Set(recommendedTools.map((r) => r.tool_id));
    return filteredTools.filter((t) => recIds.has(t.id));
  }, [filteredTools, recommendedTools]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategory, showFavoritesOnly, showFeaturedOnly, recommendedTools]);

  const totalPages = Math.max(1, Math.ceil(sortedTools.length / itemsPerPage));
  const paginatedTools = useMemo(
    () => sortedTools.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage),
    [sortedTools, currentPage],
  );

  const getRecommendationReason = (toolId: string) => {
    return recommendedTools.find((r) => r.tool_id === toolId)?.reason;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-20 text-muted-foreground">
        Error al cargar el catálogo de herramientas.
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-secondary to-primary">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            Catálogo de Herramientas IA
          </h1>
          <p className="text-muted-foreground mt-2">
            Descubre y explora las mejores herramientas de inteligencia artificial para potenciar tu trabajo
          </p>
        </div>
        <Badge variant="outline" className="gap-1.5 px-3 py-1.5">
          <Wand2 className="w-4 h-4" />
          {allTools.length} herramientas
        </Badge>
      </div>

      {/* Filters */}
      <Card className="glass-card">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            {/* Row 1: Search and AI Recommend */}
            <div className="flex flex-col gap-3 md:flex-row md:items-center">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar herramientas..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button 
                variant="outline" 
                className="gap-2 border-primary/30 hover:bg-primary/10 text-primary"
                onClick={() => isPublic ? setShowRegisterModal(true) : setShowRecommendDialog(true)}
              >
                <Lightbulb className="w-4 h-4" />
                Recomiéndame una herramienta
              </Button>
            </div>
            
            {/* Row 2: Dropdowns and Checkboxes */}
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="flex flex-wrap gap-3">
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="w-[200px]">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>


              </div>
              
              {/* Checkbox filters */}
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    checked={showFeaturedOnly}
                    onChange={(e) => setShowFeaturedOnly(e.target.checked)}
                    className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <Crown className="w-4 h-4 text-warning" />
                  <span className="text-muted-foreground">Destacadas</span>
                </label>
                {!isPublic && (
                  <label className="flex items-center gap-2 cursor-pointer text-sm">
                    <input
                      type="checkbox"
                      checked={showFavoritesOnly}
                      onChange={(e) => setShowFavoritesOnly(e.target.checked)}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
                    />
                    <Heart className="w-4 h-4 text-destructive" />
                    <span className="text-muted-foreground">Favoritos</span>
                  </label>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* AI Recommend Dialog */}
      <Dialog open={showRecommendDialog} onOpenChange={setShowRecommendDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-gradient-to-br from-primary to-secondary">
                <Lightbulb className="w-5 h-5 text-white" />
              </div>
              Recomiéndame una herramienta
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-muted-foreground">
              Describe qué quieres lograr y la IA encontrará las herramientas más adecuadas para ti.
            </p>
            <textarea
              value={recommendQuery}
              onChange={(e) => setRecommendQuery(e.target.value)}
              placeholder="Ej: Quiero crear contenido para redes sociales de manera automática..."
              className="w-full min-h-[120px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowRecommendDialog(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleAIRecommend}
                disabled={!recommendQuery.trim() || isSearchingAI}
                className="gap-2"
              >
                {isSearchingAI ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Buscando...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Buscar con IA
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Recommendations Banner */}
      {recommendedTools.length > 0 && (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              <span className="text-sm font-medium text-foreground">
                {recommendedTools.length} herramientas recomendadas por IA
              </span>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setRecommendedTools([])}>
              Limpiar recomendaciones
            </Button>
          </CardContent>
        </Card>
      )}

      {/* All Tools */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
          {showFeaturedOnly && <Crown className="w-5 h-5 text-warning" />}
          {recommendedTools.length > 0
            ? 'Herramientas Recomendadas por IA'
            : selectedCategory === 'all' && !showFeaturedOnly
              ? 'Todas las Herramientas'
              : showFeaturedOnly 
                ? 'Herramientas Destacadas'
                : 'Resultados de búsqueda'}
          <Badge variant="secondary" className="ml-2">{sortedTools.length}</Badge>
        </h2>
        {sortedTools.length > 0 ? (
          <>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {paginatedTools.map((tool) => (
                <ToolCard 
                  key={tool.id} 
                  tool={tool}
                  featured={tool.featured}
                  isFavorite={favorites.includes(tool.id)}
                  onToggleFavorite={toggleFavorite}
                  onViewDetails={setSelectedTool}
                  isPublic={isPublic}
                  aiReason={getRecommendationReason(tool.id)}
                />
              ))}
            </div>

            {totalPages > 1 && (
              <Pagination className="pt-4">
                <PaginationContent className="flex-wrap">
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                    />
                  </PaginationItem>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                    .map((page, idx, arr) => {
                      const prev = arr[idx - 1];
                      const showEllipsis = prev && page - prev > 1;
                      return (
                        <span key={page} className="flex items-center">
                          {showEllipsis && <span className="px-2 text-muted-foreground">…</span>}
                          <PaginationItem>
                            <PaginationLink
                              onClick={() => setCurrentPage(page)}
                              isActive={currentPage === page}
                              className="cursor-pointer"
                            >
                              {page}
                            </PaginationLink>
                          </PaginationItem>
                        </span>
                      );
                    })}
                  <PaginationItem>
                    <PaginationNext
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            )}
          </>
        ) : (
          <Card className="glass-card p-8 text-center">
            <Sparkles className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">No se encontraron herramientas</h3>
            <p className="text-muted-foreground">
              Intenta ajustar los filtros o busca con otros términos
            </p>
          </Card>
        )}
      </div>

      {/* Tool Detail Modal */}
      <ToolDetailModal 
        tool={selectedTool} 
        onClose={() => setSelectedTool(null)}
        isFavorite={selectedTool ? favorites.includes(selectedTool.id) : false}
        onToggleFavorite={toggleFavorite}
        isPublic={isPublic}
      />

      <RegisterToViewModal
        open={showRegisterModal}
        onOpenChange={setShowRegisterModal}
        contentName="Recomendador de herramientas IA"
      />
    </div>
  );
}

interface ToolDetailModalProps {
  tool: AITool | null;
  onClose: () => void;
  isFavorite: boolean;
  onToggleFavorite: (toolId: string, toolName: string) => void;
  isPublic?: boolean;
}

function ToolDetailModal({ tool, onClose, isFavorite, onToggleFavorite, isPublic = false }: ToolDetailModalProps) {
  if (!tool) return null;

  const categoryInfo = categories.find((c) => c.id === tool.category);

  return (
    <Dialog open={!!tool} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start gap-4">
            <div className="p-4 rounded-xl bg-gradient-to-br from-secondary to-primary text-white">
              {tool.icon}
            </div>
            <div className="flex-1">
              <DialogTitle className="text-2xl flex items-center gap-2">
                {tool.name}
                {tool.trending && (
                  <Badge variant="outline" className="gap-1 text-xs bg-accent/10 text-accent border-accent/30">
                    <TrendingUp className="w-3 h-3" />
                    Trending
                  </Badge>
                )}
              </DialogTitle>
              <div className="flex items-center gap-3 mt-2">
                <div className="flex items-center gap-1 text-warning">
                  <Star className="w-4 h-4 fill-current" />
                  <span className="font-medium">{tool.rating}</span>
                </div>
                <Badge variant="outline" className={pricingColors[tool.pricing]}>
                  {pricingLabels[tool.pricing]}
                </Badge>
                <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  {categoryInfo?.icon}
                  <span>{categoryInfo?.label}</span>
                </div>
              </div>
            </div>
            {!isPublic && (
              <Button
                variant="ghost"
                size="icon"
                className={`h-10 w-10 ${isFavorite ? 'text-destructive hover:text-destructive/80' : 'text-muted-foreground hover:text-destructive'}`}
                onClick={() => onToggleFavorite(tool.id, tool.name)}
              >
                <Heart className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
              </Button>
            )}
          </div>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Description */}
          <div>
            <p className="text-muted-foreground leading-relaxed">{tool.fullDescription}</p>
          </div>

          {/* Company Info */}
          <div className="flex flex-wrap gap-4 p-4 rounded-lg bg-muted/50">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm"><strong>Empresa:</strong> {tool.company}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm"><strong>Fundado:</strong> {tool.founded}</span>
            </div>
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-muted-foreground" />
              <a 
                href={tool.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-primary hover:underline"
              >
                {tool.url.replace('https://', '')}
              </a>
            </div>
          </div>

          {/* Tags */}
          <div>
            <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              Etiquetas
            </h4>
            <div className="flex flex-wrap gap-2">
              {tool.tags.map((tag) => (
                <Badge key={tag} variant="secondary">{tag}</Badge>
              ))}
            </div>
          </div>




          <Separator />

          {/* Use Cases */}
          <div>
            <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <Lightbulb className="w-4 h-4 text-warning" />
              Casos de Uso
            </h4>
            <ul className="grid gap-2 md:grid-cols-2">
              {tool.useCases.map((useCase, index) => (
                <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <Check className="w-4 h-4 text-success mt-0.5 shrink-0" />
                  {useCase}
                </li>
              ))}
            </ul>
          </div>

          {/* Pros and Cons */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="p-4 rounded-lg bg-success/10 border border-success/20">
              <h4 className="text-sm font-semibold mb-3 flex items-center gap-2 text-success">
                <ThumbsUp className="w-4 h-4" />
                Ventajas
              </h4>
              <ul className="space-y-2">
                {tool.pros.map((pro, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 text-success mt-0.5 shrink-0" />
                    {pro}
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/20">
              <h4 className="text-sm font-semibold mb-3 flex items-center gap-2 text-destructive">
                <ThumbsDown className="w-4 h-4" />
                Desventajas
              </h4>
              <ul className="space-y-2">
                {tool.cons.map((con, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm">
                    <span className="w-4 h-4 mt-0.5 shrink-0 text-center text-destructive">•</span>
                    {con}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Integrations */}
          <div>
            <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <Link2 className="w-4 h-4 text-primary" />
              Integraciones
            </h4>
            <div className="flex flex-wrap gap-2">
              {tool.integrations.map((integration, index) => (
                <Badge key={index} variant="outline" className="bg-background">
                  {integration}
                </Badge>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t">
            <Button 
              className="flex-1 gap-2"
              onClick={() => window.open(tool.url, '_blank')}
            >
              <ExternalLink className="w-4 h-4" />
              Visitar sitio web
            </Button>
            {!isPublic && (
              <Button 
                variant="outline"
                className={`gap-2 ${isFavorite ? 'text-destructive border-destructive/30' : ''}`}
                onClick={() => onToggleFavorite(tool.id, tool.name)}
              >
                <Heart className={`w-4 h-4 ${isFavorite ? 'fill-current' : ''}`} />
                {isFavorite ? 'Quitar de favoritos' : 'Agregar a favoritos'}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface ToolCardProps {
  tool: AITool;
  featured?: boolean;
  isFavorite: boolean;
  onToggleFavorite: (toolId: string, toolName: string) => void;
  onViewDetails: (tool: AITool) => void;
  isPublic?: boolean;
  aiReason?: string;
}

function ToolCard({ tool, featured = false, isFavorite, onToggleFavorite, onViewDetails, isPublic = false, aiReason }: ToolCardProps) {
  const categoryInfo = categories.find((c) => c.id === tool.category);

  return (
    <Card
      className={`group card-interactive overflow-hidden cursor-pointer ${
        aiReason ? 'border-primary/40 bg-gradient-to-br from-primary/5 to-secondary/5 ring-1 ring-primary/20' :
        featured ? 'border-secondary/30 bg-gradient-to-br from-secondary/5 to-primary/5' : ''
      }`}
      onClick={() => onViewDetails(tool)}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div
            className={`p-3 rounded-xl ${
              featured
                ? 'bg-gradient-to-br from-secondary to-primary text-white'
                : 'bg-muted text-foreground'
            }`}
          >
            {tool.icon}
          </div>
          <div className="flex items-center gap-2">
            {tool.trending && (
              <Badge variant="outline" className="gap-1 text-xs bg-accent/10 text-accent border-accent/30">
                <TrendingUp className="w-3 h-3" />
                Trending
              </Badge>
            )}
            {!isPublic && (
              <Button
                variant="ghost"
                size="icon"
                className={`h-8 w-8 ${isFavorite ? 'text-destructive hover:text-destructive/80' : 'text-muted-foreground hover:text-destructive'}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleFavorite(tool.id, tool.name);
                }}
              >
                <Heart className={`w-4 h-4 ${isFavorite ? 'fill-current' : ''}`} />
              </Button>
            )}
          </div>
        </div>
        <div className="pt-3">
          <CardTitle className="text-lg group-hover:text-primary transition-colors">
            {tool.name}
          </CardTitle>
          <div className="flex items-center gap-2 mt-1">
            <div className="flex items-center gap-1 text-warning">
              <Star className="w-4 h-4 fill-current" />
              <span className="text-sm font-medium">{tool.rating}</span>
            </div>
            <span className="text-muted-foreground text-xs">•</span>
            <Badge variant="outline" className={`text-xs ${pricingColors[tool.pricing]}`}>
              {pricingLabels[tool.pricing]}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {aiReason && (
          <div className="flex items-start gap-2 p-2 rounded-lg bg-primary/10 border border-primary/20">
            <Sparkles className="w-4 h-4 text-primary mt-0.5 shrink-0" />
            <p className="text-xs text-primary font-medium">{aiReason}</p>
          </div>
        )}
        <p className="text-sm text-muted-foreground line-clamp-2">{tool.description}</p>

        <div className="flex flex-wrap gap-1.5">
          {tool.tags.slice(0, 3).map((tag) => (
            <Badge key={tag} variant="secondary" className="text-xs">
              {tag}
            </Badge>
          ))}
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-border">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            {categoryInfo?.icon}
            <span>{categoryInfo?.label}</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5 text-primary hover:text-primary"
            onClick={(e) => {
              e.stopPropagation();
              onViewDetails(tool);
            }}
          >
            <span>Ver detalles</span>
            <ExternalLink className="w-3.5 h-3.5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
